import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useRouter } from 'next/navigation';
import useApiCall from '@/services/api/useApiCall';
import { buildApiConfig } from '@/services/api/apiClient';
import { endPointEnums } from '@/services/api/endpoints';
import { userProfileUpdated } from '@/app/redux/slices/userProfileSlice';
import { logout } from '@/app/redux/slices/authSlice';
import { NAVIGATION_ROUTES, USER_TYPES } from '../config';
import { FIELD_LABELS } from '../constants';

export function useProfile() {
  // State
  const dispatch = useDispatch();
  const router = useRouter();
  const { makeApiCall } = useApiCall();
  // Safely access userProfile - handle null/undefined state during user switch
  const userProfile = useSelector((state) => state.userProfile?.data?.user ?? null);
  const [active, setActive] = useState('profile');
  const [editModal, setEditModal] = useState({ open: false, field: null });
  const [editLoading, setEditLoading] = useState(false);
  const [userTypeDetails, setUserTypeDetails] = useState(USER_TYPES.INDIVIDUAL);
  const [expandedSections, setExpandedSections] = useState({
    profile: true, // Profile section expanded by default
    payment: false,
    notifications: false,
  });
  const [loading, setLoading] = useState({});
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  // Effects - Force fetch profile on mount/reload
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const config = buildApiConfig(endPointEnums.GET_USER_PROFILE, {});
        const response = await makeApiCall(config);
        if (response && response.success !== false) {
          // Dispatch the fetched profile to Redux
          dispatch({
            type: 'userProfile/userProfileRequestSuccess',
            payload: response.data,
          });
        }
      } catch (error) {
        console.error('[useProfile] Failed to fetch profile:', error);
        dispatch({
          type: 'userProfile/userProfileRequestError',
          payload: error.message,
        });
      }
    };

    // Always fetch on mount to ensure fresh data
    fetchProfile();
  }, []); // Empty deps - run once on mount

  useEffect(() => {
    setUserTypeDetails(userProfile?.userType || USER_TYPES.INDIVIDUAL);
  }, [userProfile?.userType]);

  // Handlers
  const handleNav = (key) => {
    if (key === 'history') window.location.href = NAVIGATION_ROUTES.HISTORY;
    if (key === 'support') window.location.href = NAVIGATION_ROUTES.SUPPORT;
    if (key === 'profile') return;
  };

  const handleEditClick = (field) => setEditModal({ open: true, field });
  const handleEditClose = () => {
    setEditModal({ open: false, field: null });
    setEditLoading(false);
  };

  const handleEditSave = async (newValueOrObj) => {
    if (!editModal.field) return;
    setEditLoading(true);
    let updatePayload;
    if (editModal.field.key === 'userType') {
      updatePayload = { ...newValueOrObj, userId: userProfile.userId };
    } else {
      updatePayload = {
        [editModal.field.key]: newValueOrObj,
        userId: userProfile.userId,
      };
    }
    const config = buildApiConfig(endPointEnums.UPDATE_USER_PROFILE, updatePayload);
    try {
      await makeApiCall(config);
      dispatch(userProfileUpdated(updatePayload));
      handleEditClose();
    } catch (e) {
      setEditLoading(false);
    }
  };

  const handleFieldSave = async (fieldKey, value) => {
    setLoading((prev) => ({ ...prev, [fieldKey]: true }));
    setErrors((prev) => ({ ...prev, [fieldKey]: '' }));

    try {
      const updatePayload = { [fieldKey]: value, userId: userProfile.userId };
      const config = buildApiConfig(endPointEnums.UPDATE_USER_PROFILE, updatePayload);

      // Wait for successful API response
      const response = await makeApiCall(config);

      // Only update store after successful backend response
      if (response && response.success !== false) {
        // Update the profile data with full response to keep structure consistent
        dispatch({
          type: 'userProfile/userProfileRequestSuccess',
          payload: response.data,
        });
        const label = FIELD_LABELS[fieldKey] || fieldKey;
        setSuccessMessage(`${label} updated successfully`);
        setTimeout(() => setSuccessMessage(''), 3000); // Clear success message after 3s
      } else {
        throw new Error(response?.message || 'Update failed');
      }
    } catch (error) {
      console.error('Profile update error:', error);
      setErrors((prev) => ({
        ...prev,
        [fieldKey]: error.message || 'Failed to update. Please try again.',
      }));
    } finally {
      setLoading((prev) => ({ ...prev, [fieldKey]: false }));
    }
  };

  const handleUserTypeChange = async (newType) => {
    setLoading((prev) => ({ ...prev, userType: true }));
    setErrors((prev) => ({ ...prev, userType: '' }));

    try {
      const updatePayload = { userType: newType, userId: userProfile.userId };
      const config = buildApiConfig(endPointEnums.UPDATE_USER_PROFILE, updatePayload);

      // Wait for successful API response
      const response = await makeApiCall(config);

      if (response && response.success !== false) {
        // Update with full response to maintain structure
        dispatch({
          type: 'userProfile/userProfileRequestSuccess',
          payload: response.data,
        });
        setUserTypeDetails(newType);
        setSuccessMessage('User type updated successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        throw new Error(response?.message || 'User type update failed');
      }
    } catch (error) {
      console.error('User type update error:', error);
      setErrors((prev) => ({
        ...prev,
        userType: error.message || 'Failed to update user type. Please try again.',
      }));
    } finally {
      setLoading((prev) => ({ ...prev, userType: false }));
    }
  };

  const toggleSection = (sectionKey) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const clearError = (fieldKey) => {
    setErrors((prev) => ({ ...prev, [fieldKey]: '' }));
  };

  const handleLogout = async () => {
    // Show confirmation dialog first
    setShowLogoutDialog(true);
  };

  const confirmLogout = async () => {
    setLoggingOut(true);
    setShowLogoutDialog(false);

    // Do cleanup first
    try {
      const { createLogoutHandler } = await import('@/utils/logoutUtils');
      const logoutHandler = createLogoutHandler(dispatch);
      await logoutHandler({
        onComplete: () => {
          // Redirect after cleanup
          window.location.href = '/login';
        },
      });
    } catch (error) {
      console.error('[Profile] Logout error:', error);
      // Redirect even on error
      window.location.href = '/login';
    }
  };

  const cancelLogout = () => {
    setShowLogoutDialog(false);
  };

  return {
    // State
    user: userProfile || {},
    active,
    editModal,
    editLoading,
    userTypeDetails,
    expandedSections,
    loading,
    errors,
    successMessage,
    showLogoutDialog,
    loggingOut,

    // Handlers
    handleNav,
    handleEditClick,
    handleEditClose,
    handleEditSave,
    handleFieldSave,
    handleUserTypeChange,
    handleLogout,
    confirmLogout,
    cancelLogout,
    toggleSection,
    clearError,
  };
}
